/**
 * **房间级的大厅协议。**
 *
 * 它骑在 `S3Peer` 上（**随房间**，不是随对局），把 `src/ui/room.ts` 那份
 * **纯大厅模型**广播出去。分成四件事：
 *
 * | 方法 | 谁发 | 带什么 | 为什么 |
 * | --- | --- | --- | --- |
 * | `lobby/presence` | 每个人，定期 | 昵称 / 准备 / 是不是创建者 | 名册与准备状态 |
 * | `lobby/config` | **只有创建者** | 整份 `RoomConfig` | 游戏规则只有一个权威 |
 * | `lobby/kick` | **只有创建者** | 被踢的人 | 权限 |
 * | `lobby/start` | **只有创建者** | 种子 | 开局 |
 *
 * ## 为什么是"定期广播"而不是"请求-响应"
 *
 * 房间期没有人是权威（对局房主要等开局才选出来）。而大厅的状态是**很小**的
 * 一份东西（昵称 + 一个布尔 + 一份游戏规则），所以最省事也最稳的做法是
 * 每个人周期性地把**自己的那一份**喊出去 —— 丢一包没关系，下一包会补上。
 *
 * ⚠️ 这**不是**"轮询"：没有人在问，是每个人在说。它和心跳是同一个形状，
 * 而且比心跳还便宜（几个人、几个字节）。
 *
 * ## 为什么不用旁路通道（`net/side.ts`）
 *
 * 旁路通道是"不进状态的表现层数据"（拖拽帧、hover）。
 * 大厅的这些东西**要进模型** —— 它们决定谁能改游戏规则、谁能开局。
 * 所以走的是普通通知，而且带 zod 校验。
 */

import { z } from 'zod';
import { t } from '../i18n';
import type { S3Channel } from '../net/peer';
import { createSideChannel } from '../net/side';
import type { RoomConfig } from '../core/config';
import { MAX_PLAYERS } from '../core/config';
import type { LobbyState, PackRef } from '../ui/room';
import type { PackId } from '../core/ids';
import { applyRemoteBot, applyRemoteConfig, applyRemotePlayer, creatorId, findPlayer, isSelfCreator, leaveLobby } from '../ui/room';

/** 大厅的方法名 */
export const LOBBY_PRESENCE = 'lobby/presence';
export const LOBBY_CONFIG = 'lobby/config';
export const LOBBY_KICK = 'lobby/kick';
export const LOBBY_START = 'lobby/start';
/** 创建者：加了一个机器人席位 */
export const LOBBY_BOT = 'lobby/bot';
/**
 * **手动刷新。** 任何一个人都能广播它，要求别人**立刻**重播一次 presence
 * （而不是等下一秒的定时广播），创建者顺带把权威配置再发一遍。
 *
 * 存在的意义：房间页那个「刷新同步」按钮 —— 当某个人漏收了一条 presence
 * 卡在旧名册/旧配置上时，按一下就能对齐，不必退出重进。
 */
export const LOBBY_REFRESH = 'lobby/refresh';

/** 我这一份。**小到可以定期重发** */
export const PresenceSchema = z.object({
  nickname: z.string(),
  ready: z.boolean(),
  isCreator: z.boolean(),
  /** 本机装了哪些内容包（id + 内容指纹）。大厅据此做一致性校验 */
  packs: z.array(z.object({ id: z.string(), hash: z.string() })),
  /**
   * **完整的 git commit hash。** 进房时和房主比对：对不上就不让进。
   *
   * ⚠️ 老客户端没有这一项 —— 缺省空串，照样按"对不上"处理（它本来就不该
   *    和改了协议的新房主同房）。
   */
  commit: z.string().default(''),
  /**
   * **我这一行 RTT**（我到房间里每个人的延迟，毫秒）。
   *
   * ⚠️ 没有它，对局房主就选不出来。第一版只带 `nickname / ready`，
   * 于是每个人拿到的"延迟表"里**只有自己那一格**是实数、
   * 别人全是未知 —— 而未知按最差算，所以**每个人都算出自己是房主**。
   * 表现是两个对局各开各的、互不相认。
   *
   * 把每个人那一行拼起来就是完整的 RTT 矩阵，`electHost()` 才有意义
   * （它是纯函数，同一份矩阵所有人算出同一个答案）。
   */
  rtts: z.record(z.string(), z.number()),
});
export type Presence = z.infer<typeof PresenceSchema>;

/**
 * 游戏规则。
 *
 * ⚠️ 这里**宽**（`looseObject`）：游戏规则的字段会跟着游戏演化，而多一个字段
 * 不该让老客户端把整包丢掉。真正的把关在**开局那一刻**的
 * `validateConfig()` —— 它才是权威（`docs/rules.md` §11）。
 */
export const ConfigSchema = z.looseObject({
  playerCount: z.number().int().optional(),
  openingHandSize: z.number().int().optional(),
  openingRescueCount: z.number().int().optional(),
  deck: z.record(z.string(), z.number().int()).optional(),
});
export const RefreshSchema = z.object({});
export const KickSchema = z.object({ target: z.string().min(1), reason: z.string().optional() });
export const BotSchema = z.object({ id: z.string().min(1), nickname: z.string() });
export const StartSchema = z.object({
  seed: z.number().int(),
  /** 创建者选出来的对局房主。**所有人照它走** */
  host: z.string().min(1).nullable(),
  /**
   * 创建者手里的**权威游戏规则**。
   *
   * ⚠️ 游戏规则平时是"改完广播一次"的，后进房的人收不到 —— 万一那个人恰好是
   *    对局房主，开局就会用默认牌堆（用户报的"改了牌堆构成但记牌器还是按
   *    正常牌堆算"）。所以开局这一下把它**随 START 一起再发一遍**。
   */
  config: ConfigSchema.optional(),
});

/** 定期重发我的状态的间隔（毫秒）。1 秒足够一个人进房被看见 */
const ANNOUNCE_MS = 1000;

export interface LobbyNetOptions {
  readonly channel: S3Channel;
  /** 我的 peer id */
  readonly selfId: string;
  /** 当前的大厅状态。**每次要发之前现取** —— 状态是会被改的 */
  readonly snapshot: () => LobbyState | null;
  /** 我这一行 RTT（我到房间里每个人的延迟）。**选举靠它拼矩阵** */
  readonly rttRow: () => Record<string, number>;
  /** 本机装了哪些内容包（id + 内容指纹）。随 presence 一起广播 */
  readonly localPacks: () => readonly PackRef[];
  /** 本机的完整 git commit hash。进房要和房主比对 */
  readonly localCommit: () => string;
  /** 收到远端的东西之后，把新的大厅状态交回给上面 */
  readonly apply: (next: LobbyState, from: string) => void;
  /** 被创建者踢了 */
  readonly onKicked: (by: string, reason?: string) => void;
  /** 和房主版本对不上，进不了房 */
  readonly onVersionMismatch: (reason: string) => void;
  /** 创建者宣布开局；`config` 是它手里的权威游戏规则（可能没有） */
  readonly onStart: (seed: number, host: string | null, config: RoomConfig | null) => void;
  /** 观测：远端报障或出错 */
  readonly onError?: (error: unknown) => void;
}

export interface LobbyNet {
  /** 立刻广播一次（进房、改昵称、改准备、改游戏规则之后调） */
  announce(): void;
  /** **手动刷新**：让别人立刻重播 presence，创建者补发权威配置 */
  refresh(): void;
  /** 我这一行 RTT（每次 announce 现取） */
  readonly rttRow: () => Record<string, number>;
  /** 累计下来的 RTT 矩阵：`matrix[上报者][目标]` */
  readonly rttMatrix: () => Record<string, Record<string, number>>;
  /** 开始定期广播 */
  start(): void;
  stop(): void;
  /** 创建者广播整份游戏规则 */
  broadcastConfig(config: RoomConfig): void;
  /** 创建者踢人 */
  kick(target: string): void;
  /** 创建者加了一个机器人席位（无 peer，房主代打） */
  addBot(id: string, nickname: string): void;
  /** 创建者宣布开局。`host` 是它选出来的对局房主，`config` 是权威游戏规则 */
  startGame(seed: number, host: string | null, config?: RoomConfig | null): void;
  dispose(): void;
}

export function createLobbyNet(options: LobbyNetOptions): LobbyNet {
  // ⚠️ 走的是 `notify`（广播），不是 `request`：大厅期没有权威，
  //    谁都不该等谁回应。所以这里复用的是旁路通道那层薄壳 ——
  //    它本来就是"广播一条通知"的意思，只是名字叫 side。
  const wire = createSideChannel(options.channel);
  const offs: (() => void)[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;

  const mine = (): Presence => {
    const state = options.snapshot();
    const me = state === null ? null : findPlayer(state, options.selfId);
    return {
      nickname: me?.nickname ?? '',
      ready: me?.ready ?? false,
      // 创建者身份以**本地模型**为准：创建房间那一刻就定下来了
      isCreator: me?.isCreator ?? false,
      rtts: options.rttRow(),
      packs: options.localPacks().map((pack) => ({ id: pack.id, hash: pack.hash })),
      commit: options.localCommit(),
    };
  };

  /**
   * **版本闸门。** 永远和房主比 commit：
   *
   * - 我是创建者 → 对不上的进来一个踢一个；
   * - 对方是创建者（房主）→ 我自己退出，进不了房。
   *
   * 对不上的一律**不并进名册**。
   */
  const versionOk = (from: string, presence: Presence): boolean => {
    const mine = options.localCommit();
    if (presence.commit === mine) return true;
    const state = options.snapshot();
    if (state === null) return false;
    if (isSelfCreator(state)) {
      wire.broadcast(LOBBY_KICK, { target: from, reason: t('host.lobby.versionMismatchKick') });
    } else if (presence.isCreator) {
      options.onVersionMismatch(t('host.lobby.versionMismatch'));
    }
    return false;
  };

  /** 别人上报的那一行行。拼起来就是矩阵 */
  const rows = new Map<string, Record<string, number>>();

  const announce = (): void => {
    if (options.snapshot() === null) return;
    wire.broadcast(LOBBY_PRESENCE, mine());
  };

  /** 手动刷新：喊一声 + 立刻重播自己（不占额外的定时器） */
  const refresh = (): void => {
    if (options.snapshot() === null) return;
    wire.broadcast(LOBBY_REFRESH, {});
    announce();
  };

  const applyPresence = (from: string, presence: Presence): void => {
    if (!versionOk(from, presence)) return;
    const state = options.snapshot();
    if (state === null) return;
    // ★ 满员：新来的直接踢出去（创建者是权威）
    if (!state.players.some((p) => p.id === from) && state.players.length >= MAX_PLAYERS) {
      if (isSelfCreator(state)) {
        wire.broadcast(LOBBY_KICK, { target: from, reason: t('host.lobby.roomFull', { n: MAX_PLAYERS }) });
      }
      return;
    }
    rows.set(from, presence.rtts);
    const packs: PackRef[] = presence.packs.map((pack) => ({ id: pack.id as PackId, hash: pack.hash }));
    const known = state.players.some((player) => player.id === from);
    const next = applyRemotePlayer(state, { id: from, ...presence, packs });
    options.apply(next, from);
    // ★ 后进来的人手里只有本地默认规则 —— 创建者把权威配置补发一次。
    //   只有创建者算数（applyConfig 会挡掉别人），且只在刚出现的那一次发，
    //   否则每秒一次 presence 会顺带轰炸一遍配置。
    if (!known && isSelfCreator(next)) wire.broadcast(LOBBY_CONFIG, next.config);
  };

  const applyConfig = (from: string, config: RoomConfig): void => {
    const state = options.snapshot();
    if (state === null) return;
    // 游戏规则只认创建者发来的 —— 别人发的一律无视
    if (creatorId(state) !== from) return;
    options.apply(applyRemoteConfig(state, config), from);
  };

  const applyKick = (from: string, target: string, reason?: string): void => {
    const state = options.snapshot();
    if (state === null) return;
    if (creatorId(state) !== from) return;
    if (target !== options.selfId) return;
    options.onKicked(from, reason);
  };

  const applyBot = (from: string, bot: { readonly id: string; readonly nickname: string }): void => {
    const state = options.snapshot();
    if (state === null) return;
    if (creatorId(state) !== from) return;
    options.apply(applyRemoteBot(state, bot, options.localPacks()), from);
  };

  const applyStart = (
    from: string,
    seed: number,
    host: string | null,
    config: RoomConfig | null,
  ): void => {
    const state = options.snapshot();
    if (state === null) return;
    if (creatorId(state) !== from) return;
    options.onStart(seed, host, config);
  };

  offs.push(
    wire.on(LOBBY_PRESENCE, PresenceSchema, (payload, from) => {
      if (from === options.selfId) return; // 自己的回声，不认
      applyPresence(from, payload);
    }),
    wire.on(LOBBY_CONFIG, ConfigSchema, (payload, from) => {
      applyConfig(from, payload as unknown as RoomConfig);
    }),
    wire.on(LOBBY_KICK, KickSchema, (payload, from) => {
      applyKick(from, payload.target, payload.reason);
    }),
    wire.on(LOBBY_BOT, BotSchema, (payload, from) => {
      applyBot(from, payload);
    }),
    wire.on(LOBBY_REFRESH, RefreshSchema, (_payload, from) => {
      if (from === options.selfId) return; // 自己的回声
      // ★ 别人手动刷新：我**立刻**重播一次 presence（比等下一秒快）；
      //   创建者顺带把权威配置再发一遍 —— 那正是「配置卡在旧版本」的解药。
      announce();
      const state = options.snapshot();
      if (state !== null && isSelfCreator(state)) wire.broadcast(LOBBY_CONFIG, state.config);
    }),
    wire.on(LOBBY_START, StartSchema, (payload, from) => {
      applyStart(from, payload.seed, payload.host, (payload.config as RoomConfig | undefined) ?? null);
    }),
  );

  // ★ 有人从链路上消失了，就把他从名册里删掉。
  //
  //   ⚠️ 以前这里**什么都没接**，于是退出的人永远留在房间里：`LOBBY_PRESENCE`
  //   只会加/改，没人负责删；而 `MAX_PLAYERS` 是按名册长度算的，幽灵还会一直占座。
  //   走 `leaveLobby()`（和踢人同一条纯函数，创建者身份转移的兜底也在那边）。
  //   mesh 里每个 peer 都直接看见离开，各自删一遍即可，不用再广播。
  const removePeer = (from: string): void => {
    rows.delete(from);
    const state = options.snapshot();
    if (state === null) return;
    if (!state.players.some((player) => player.id === from)) return;
    options.apply(leaveLobby(state, from), from);
  };
  offs.push(
    // 链路真的断了（关窗 / 掉线）
    options.channel.onPeerLeave((id) => removePeer(id)),
    // 链路还在、心跳判死（半开连接）。大厅期没有「掉线但留座」这回事，
    // 留着他会一直占一个座；通了之后他会重新广播 presence，自然又加回来。
    // `onStateChange` 只在状态真的变化时发，所以这不是每秒一次。
    options.channel.onPeerState((id, peerState) => {
      if (peerState === 'down') removePeer(id);
    }),
  );

  return {
    announce,
    refresh,
    rttRow: () => options.rttRow(),
    rttMatrix: () => {
      const out: Record<string, Record<string, number>> = { [options.selfId]: options.rttRow() };
      for (const [from, row] of rows) out[from] = row;
      return out;
    },
    start() {
      if (timer !== null) return;
      announce();
      timer = setInterval(announce, ANNOUNCE_MS);
    },
    stop() {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    },
    broadcastConfig(config) {
      wire.broadcast(LOBBY_CONFIG, config);
    },
    kick(target) {
      wire.broadcast(LOBBY_KICK, { target });
    },
    addBot(id, nickname) {
      wire.broadcast(LOBBY_BOT, { id, nickname });
    },
    startGame(seed, host, config) {
      wire.broadcast(LOBBY_START, { seed, host, config: config ?? undefined });
    },
    dispose() {
      this.stop();
      for (const off of offs) off();
      offs.length = 0;
    },
  };
}
